/* eslint-disable react/jsx-props-no-spreading */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FormControl, OutlinedInput, InputLabel, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import makeStyles from '@mui/styles/makeStyles';
import { AttachFile, GridOn } from '@mui/icons-material';
import cn from 'classnames';
import { VideoSessionDocuments, AppointmentAttachments } from 'components/modules';
import { useModal, useProfile } from 'hooks';
import config from 'config';
import Styles from './styles';

const useStyles = makeStyles(Styles);

function VideoSessionWhereby({ videoSessionId, appointmentId, data }) {
  const classes = useStyles();
  const { data: profile } = useProfile();
  const { t } = useTranslation('labels');
  const { organizationId, wherebyUrl, patientName, adracareSessionToken } = data;
  const [roomUrl, setRoomUrl] = useState('');
  const [userName, setUserName] = useState(patientName);
  const [preCallSetup, setPreCallSetup] = useState(true);
  const { handleModal } = useModal();
  const [showPlatformButtons, setShowPlatformButtons] = useState(false);
  const toggleDocumentModal = async () => {
    let documentModal = (
      <VideoSessionDocuments
        sessionId={videoSessionId}
        sessionToken={adracareSessionToken}
        files={data?.appointmentFilesMeta || []}
        attachmentUploadEnabled={data?.fileUploadEnabled}
      />
    );

    if (profile) {
      documentModal = <AppointmentAttachments files={data?.appointmentFilesMeta || []} id={data?.appointmentId} attachmentUploadEnabled={data?.fileUploadEnabled} />;
    }

    handleModal(documentModal, { size: 'small', removeOutsideClick: true });
  };

  useEffect(() => {
    const options = [
      ['locking', 'off'],
      ['moreButton', 'off'],
      ['roomIntegrations', 'off'],
      ['timer', 'on'],
      ['fileSharing', 'off'],
      ['displayName', userName],
      ['externalId', adracareSessionToken ?? videoSessionId],
    ];
    const params = options.reduce((query, option) => `${query}${option.join('=')}&`, '').slice(0, -1);
    setRoomUrl(`${wherebyUrl}?${params}`);
  }, [userName]);

  const leaveCall = () => {
    const url = `/direct/appointments/end?oid=${organizationId}&keep_session=true&id=${appointmentId}`;
    window.location.href = url.toString();
  };

  const joinCall = () => {
    setShowPlatformButtons(true);
  };

  const updateUsername = () => {
    setPreCallSetup(false);
  };

  const openWhiteboard = () => {
    const whereby = document.querySelector('whereby-embed');
    whereby.toggleScreenshare();
    window.open(config.whiteboardUrl);
  };

  const addWherebyEvents = () => {
    const whereby = document.querySelector('whereby-embed');
    whereby.addEventListener('leave', leaveCall.bind(this));
    whereby.addEventListener('join', joinCall.bind(this));
    try {
      window.Intercom('update', { alignment: 'left' });
    } catch (err) {
      // TODO: show Toast
    }
  };

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://cdn.srv.whereby.com/embed/v2-embed.js';
    script.async = true;
    script.onload = () => {
      // TODO: need to trigger event
    };
    script.onerror = () => {
      // TODO: need to trigger event
    };
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  useEffect(() => {
    if (preCallSetup === false) {
      addWherebyEvents();
    }
  }, [preCallSetup]);

  return (
    <>
      <div className={cn(classes.videoSession, classes.videoSessionWhereby)}>
        {preCallSetup ? (
          <div className={classes.precallPanel}>
            <div className={classes.precallFields}>
              <FormControl fullWidth>
                <InputLabel htmlFor="outlined-name">{t('name')}</InputLabel>
                <OutlinedInput id="outlined-name" label={t('name')} value={userName} onChange={(e) => setUserName(e.target.value)} />
              </FormControl>
            </div>
            <div className={classes.precallFooter}>
              <Button
                data-testid="save-button"
                variant="contained"
                onClick={() => {
                  updateUsername();
                }}
                color="primary"
                fullWidth
              >
                {t('save')}
              </Button>
            </div>
          </div>
        ) : (
          <whereby-embed room={roomUrl} />
        )}
      </div>
      {showPlatformButtons && (
        <div className={classes.platformActionsButtons}>
          <div className={classes.platformActionsButton} onClick={openWhiteboard} onKeyDown={openWhiteboard} role="button" tabIndex="0">
            <GridOn />
          </div>
          <div className={classes.platformActionsButton} onClick={toggleDocumentModal} onKeyDown={toggleDocumentModal} role="button" tabIndex="0">
            <AttachFile />
          </div>
        </div>
      )}
    </>
  );
}

VideoSessionWhereby.propTypes = {
  appointmentId: PropTypes.string,
  videoSessionId: PropTypes.string,
  data: PropTypes.objectOf(PropTypes.any).isRequired,
};

VideoSessionWhereby.defaultProps = {
  appointmentId: '',
  videoSessionId: '',
};

export default VideoSessionWhereby;
